/**
 * Trial Welcome Modal
 * Shows to new users when they first sign up
 * Matching mobile app behavior
 */

import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { handleCheckout } from "@/shared/utils/checkoutHandler";
import { apiClient } from "@/shared/services/apiClient";

interface TrialWelcomeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trialDays?: number;
}

export function TrialWelcomeModal({ open, onOpenChange, trialDays = 15 }: TrialWelcomeModalProps) {
  const { toast } = useToast();
  const { usage, refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const checkoutCleanupRef = useRef<(() => void) | null>(null);

  // Mark modal as shown when it actually opens (not before)
  useEffect(() => {
    if (open) {
      console.log('[TrialWelcomeModal] 🎯 Modal opened for regular trial user:', {
        trialDays,
        isTrialActive: usage?.isTrialActive,
        subscribed: usage?.subscribed
      });

      // Set localStorage to mark today as shown (only when modal actually opens)
      const markAsShownToday = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const today = new Date().toDateString();
            const storageKey = `trial_modal_last_shown_${user.id}`;
            localStorage.setItem(storageKey, today);
            console.log('[TrialWelcomeModal] ✅ Marked as shown for today:', today);
          }
        } catch (error) {
          console.error('[TrialWelcomeModal] Error marking modal as shown:', error);
        }
      };

      markAsShownToday();
    }
  }, [open, usage, trialDays]);

  // Cleanup checkout handler on unmount
  useEffect(() => {
    return () => {
      if (checkoutCleanupRef.current) {
        checkoutCleanupRef.current();
        checkoutCleanupRef.current = null;
      }
    };
  }, []);

  const handleUpgradeToProTrial = async () => {
    console.log('[TrialWelcomeModal] User clicked "Upgrade to Pro Trial" - creating checkout session');
    setLoading(true);
    try {
      // Get user data for checkout
      const { data: { user } } = await supabase.auth.getUser();

      // Call Stripe checkout with base plan (1 user, monthly)
      // Use VITE_APP_BASE_URL for production or window.location.origin for local dev
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
      const data = await apiClient.post<{ url: string }>('/edge/create-checkout', {
        userCount: 1,
        interval: 'monthly',
        userData: {
          id: user?.id,
          email: user?.email,
          user_metadata: user?.user_metadata
        },
        // Redirect back to success/error pages (NOT billing page)
        successUrl: `${baseUrl}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${baseUrl}/subscription-error?message=Payment was canceled`
      });

      if (!data?.url) throw new Error('No checkout URL returned');

      // Close welcome modal and show processing state
      onOpenChange(false);
      setLoading(false);
      setProcessingPayment(true);

      // Use unified checkout handler
      const { cleanup } = handleCheckout({
        checkoutUrl: data.url,
        onSuccess: async () => {
          console.log('[TrialWelcomeModal] ✅ Payment successful!');

          // Refresh subscription data
          await refreshSubscription();

          // Show success toast
          toast({
            title: "Pro Trial Started!",
            description: "Welcome to wlkthru Pro! Your 15-day trial is now active.",
          });

          setProcessingPayment(false);
        },
        onError: (errorMessage) => {
          console.log('[TrialWelcomeModal] ❌ Payment failed:', errorMessage);

          // Show error toast
          toast({
            title: "Payment Failed",
            description: errorMessage || "Please try again or contact support.",
            variant: "destructive",
          });

          setProcessingPayment(false);
          onOpenChange(true); // Reopen modal
        },
        onClose: () => {
          console.log('[TrialWelcomeModal] Checkout window closed');

          // Refresh to check if payment completed
          refreshSubscription();

          setProcessingPayment(false);
          onOpenChange(true); // Reopen modal
        },
      });

      // Store cleanup function
      checkoutCleanupRef.current = cleanup;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive"
      });
    } finally {
      // Always reset loading state, even when errors occur
      setLoading(false);
    }
  };

  const proFeatures = [
    "Unlimited video calls",
    "Advanced scheduling & reminders",
    "Unlimited recordings",
    "AI-powered summaries & transcripts",
    "Priority support"
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent
        className="sm:max-w-2xl border-2 border-primary/20 z-50 p-6"
        style={{
          // Force dialog to render on top with blurred backdrop
          position: 'fixed',
        }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-3xl font-bold text-center" style={{ color: '#94ACF8' }}>
            Welcome to wlkthru! 🎉
          </DialogTitle>
          <DialogDescription className="sr-only">
            Trial welcome
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center py-4">
          {/* Pro Trial Option - Centered */}
          <div className="max-w-md w-full border-2 rounded-xl p-6 space-y-4 flex flex-col relative overflow-hidden" style={{ borderColor: '#94ACF8' }}>


            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">Pro Trial</h3>
                <div className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full text-xs font-semibold">
                  {trialDays} days free
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Get unlimited access to everything</p>
            </div>

            <div className="space-y-2 flex-1">
              {proFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <span className="mt-0.5" style={{ color: '#94ACF8' }}>✓</span>
                  <p className="text-sm text-foreground">{feature}</p>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <Button
                className="w-full text-white font-semibold py-6 text-base shadow-lg"
                style={{ backgroundColor: '#94ACF8' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7A9AE8'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#94ACF8'}
                onClick={handleUpgradeToProTrial}
                disabled={loading || processingPayment}
              >
                {loading ? "Loading..." : processingPayment ? "Processing..." : `Start Free ${trialDays}-Day Pro Trial`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
